import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { recordPasswordAccess } from "../../store/thunks/uploadsThunk";
import {
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import EmailModal from "./EmailModal";


const FileExplorer = ({
  files,
  clientId,
  client,
  onFileClick,
  onDeleteFile,
  getFileContent,
  onSendEmail,
}) => {
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loading, setLoading] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [emailStatus, setEmailStatus] = useState("");
  const { user } = useSelector((state) => state.auth);
  const roles = useSelector((state) => state.auth.user.role || []);
  // const client = useSelector((state) => state.clients.currentClient);

  console.log("roles", roles);
  console.log("client", client);
  console.log("clientId", clientId);
  const dispatch = useDispatch();

  const handleFileClick = (fileName) => {
    onFileClick(fileName);
  };

  const handleTogglePassword = async (
    filePath,
    password,
    isPasswordProtected
  ) => {
    if (!isPasswordProtected) return;

    if (revealedPasswords[filePath]) {
      // Hide password if it's already revealed
      setRevealedPasswords((prev) => ({
        ...prev,
        [filePath]: null,
      }));
    } else {
      // Show password after successful password access recording
      setLoading((prev) => ({
        ...prev,
        [filePath]: true,
      }));

      try {
        const response = await dispatch(
          recordPasswordAccess({
            clientId,
            fileName: filePath,
            userId: user._id,
          })
        ).unwrap();

        if (response.success) {
          setRevealedPasswords((prev) => ({
            ...prev,
            [filePath]: password,
          }));
        }
      } catch (error) {
        console.error("Error recording password access:", error);
      } finally {
        setLoading((prev) => ({
          ...prev,
          [filePath]: false,
        }));
      }
    }
  };

  const handleSendEmail = async (file) => {
    console.log("Opening email modal for file:", file);
    setSelectedFile(file);
    setShowEmailModal(true);
  };

  const handleConfirmSendEmail = async (customMessage) => {
    console.log("Confirming email send for file:", selectedFile);
    if (!selectedFile || !client?.email) {
      console.log("Missing required data:", {
        selectedFile,
        clientEmail: client?.email,
      });
      return;
    }

    setEmailStatus("sending");
    try {
      const result = await onSendEmail(selectedFile, customMessage);
      console.log("Email send result:", result);

      if (result.success) {
        setEmailStatus("success");
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailStatus("");
          setSelectedFile(null);
        }, 2000);
      } else {
        throw new Error(result.error || "Failed to send email");
      }
    } catch (error) {
      console.error("Error in handleConfirmSendEmail:", error);
      setEmailStatus("error");
    }
  };

  const handleCancelEmail = () => {
    setShowEmailModal(false);
    setEmailStatus("");
    setSelectedFile(null);
  };


  const renderRows = () => {
    return files.map((file, index) => {
      const fileType = file.path.split(".").pop();
      const folder = file.path.split("/")[0];
      const isPasswordProtected = file.isPasswordProtected;
      const isPasswordRevealed = revealedPasswords[file.path];
      const isLoading = loading[file.path];

      return (
        <tr key={file.path} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
          <td className="py-3 px-4">
            <div className="flex items-center">
              <span
                className="text-gray-900 hover:text-gray-700 cursor-pointer font-medium transition-colors"
                onClick={() => handleFileClick(file.path)}>
                {file.path.split("/").pop()}
              </span>
              {isPasswordProtected && (
                <div className="flex items-center ml-3">
                  <button
                    onClick={() =>
                      handleTogglePassword(
                        file.path,
                        file.password,
                        isPasswordProtected
                      )
                    }
                    disabled={isLoading}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-sm">
                    {isPasswordRevealed ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : isLoading ? (
                      <span className="text-xs">Loading...</span>
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                  {isPasswordRevealed && (
                    <span className="text-xs text-gray-500 ml-2 font-mono bg-gray-100 px-2 py-0.5 rounded-sm">
                      {file.password}
                    </span>
                  )}
                </div>
              )}
            </div>
          </td>
          <td className="py-3 px-4 text-sm text-gray-600">{folder}</td>
          <td className="py-3 px-4 text-sm text-gray-600">
            {new Date(file.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </td>
          <td className="py-3 px-4 text-sm text-gray-600">
            {file.uploadedBy ? file.uploadedBy.name : "Unknown"}
          </td>
          <td className="py-3 px-4">
            <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-100 text-gray-800">
              {fileType.toUpperCase()}
            </span>
          </td>
          {roles.includes("owner") && (
            <td className="py-3 px-4">
              <div className="flex items-center gap-2">
                <button
                  className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded-sm hover:bg-red-50"
                  onClick={() => onDeleteFile(file.path)}
                  title="Delete file">
                  <TrashIcon className="w-4 h-4" />
                </button>
                <button
                  className="text-gray-400 hover:text-blue-600 transition-colors p-1 rounded-sm hover:bg-blue-50"
                  onClick={() => handleSendEmail(file)}
                  title="Send via email">
                  <EnvelopeIcon className="w-4 h-4" />
                </button>
              </div>
            </td>
          )}
        </tr>
      );
    });
  };

  return (
    <div data-component="FileExplorer" className="bg-white rounded-sm shadow-sm border border-gray-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                File Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                Folder
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                Upload Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                Uploaded By
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                File Type
              </th>
              {roles.includes("owner") && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {files && files.length > 0 ? (
              renderRows()
            ) : (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-gray-100">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No files</h3>
                    <p className="mt-1 text-sm text-gray-500">No files are available in this location.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <EmailModal
        isOpen={showEmailModal}
        onClose={handleCancelEmail}
        onSend={handleConfirmSendEmail}
        selectedFile={selectedFile}
        client={client}
        emailStatus={emailStatus}
      />
    </div>
  );
};

export default FileExplorer;
